import {
  attachmentDetails,
  getEventAttachmentLinks,
  handlePluralString,
  isInitialTriggerText,
  isPresetNoRecipientsAutomations,
  NON_SUBSCRIBER_REPLY_CAMPAIGN_ID,
  numDetectedExternalLinks,
  splitPluginTextInputMessage,
  massTextEmptyPlaceholder,
  subscribeProfileDefault,
  containsTrustedMarkitEventLink,
  userRelatedProducts,
  getMassTextAttachmentLink,
  defaultProductListView,
  detectedSingleLinkNotAtEnd,
} from "@markit/common.utils";
import { Colors } from "../../../../utils/colors";
import { Icon } from "@iconify/react";
import { ThemeStyle } from "../../../../hooks/useTheme";
import {
  AttachmentSourceType,
  Campaign,
  DEFAULT_EVENT_TEXTS_CAMPAIGN_ID,
  DEFAULT_LINK_SUBMISSION_TEXTS_CAMPAIGN_ID,
  Event,
  EventType,
  MassText,
  MassTextAttachment,
  MassTextAttachmentId,
  MassTextAttachmentType,
  MassTextType,
  NotificationType,
} from "@markit/common.types";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { getEventState } from "../../../../redux/slices/eventSlice";
import { InsertPluginTextInput } from "./InsertPluginTextInput";
import { AttachmentsActionBar } from "./AttachmentsActionBar";
import ConfirmDeleteModal from "../../../Containers/ConfirmPopups/ConfirmDeleteModal";
import ComplianceMessageTag from "../../../Compliance/ComplianceMessageTag";
import useAsyncOnMount from "../../../../hooks/useAsyncEffectOnMount";
import { CircularProgress } from "@mui/material";
import TextCreditsNotice from "./TextCreditsNotice";
import PopupModalMessageTag from "../../../Containers/PopupModalMessageTag";
import { getVisibleTextPluginOptions } from "../../../../utils/textingUtils";
import StandardBorderedContainer from "../../../Containers/StandardBorderedContainer";
import MassTextAttachmentsPanel from "./MassTextAttachmentsPanel";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import ExpandableBox from "../../../Containers/ExpandableBox";
import DropdownArrowIcon from "../../../DropdownsAndTabs/DropdownArrowIcon";
import AlertContainer from "../../../Containers/AlertContainer";
import { TextComposerCard } from "../TextComposer/TextComposerCard";

type MassTextMessageDraftingProps = {
  campaign: Campaign | undefined;
  massText: MassText;
  updateMassTextSettings: (massText: Partial<MassText>) => void;
  numCredits: number;
  numFollowersNotCompliant: number;
  showComplianceCreditNotice: boolean;
  convertedMessage: string;
  finalMessageWithAppendages: string;
  disableEventLinks: boolean;
  massTextAttachments?: MassTextAttachment[];
  event?: Event;
  theme?: ThemeStyle;
};

export const MassTextMessageDrafting = (
  props: MassTextMessageDraftingProps
) => {
  const {
    campaign,
    massText,
    updateMassTextSettings,
    numCredits,
    numFollowersNotCompliant,
    showComplianceCreditNotice,
    convertedMessage,
    finalMessageWithAppendages,
    disableEventLinks,
    massTextAttachments,
    event,
    theme,
  } = props;
  const { accountData } = useSelector(getAccountState).account;
  const { events: eventList } = useSelector(getEventState);
  const { events, tickets } = eventList;
  const dispatch = useDispatch();
  const [defaultAttachmentListView, setDefaultAttachmentListView] = useState(0);
  const [defaultAttachmentId, setDefaultAttachmentId] = useState<
    string | undefined
  >(undefined);
  const [minimizeBanners, setMinimizeBanners] = useState(false);
  const [duplicateAlert, setDuplicateAlert] = useState(false);

  const styles = {
    smallSubtext: { color: Colors.GRAY1, fontWeight: 400, fontSize: 12 },
    subtext: { color: Colors.GRAY1, fontWeight: 400, fontSize: 14 },
    itemContainer: {
      backgroundColor: Colors.WHITE1,
      padding: 14,
      borderRadius: 12,
    },
    blueIconContainer: {
      width: 43,
      height: 43,
      borderRadius: 8,
      backgroundColor: Colors.BLUE5 + "1a",
      border: `0.5px solid ${Colors.BLUE5}`,
    },
  };

  const [showAttachmentModal, setShowAttachmentModal] =
    useState<boolean>(false);
  const [cursorPosition, setCursorPosition] = useState(massText.message.length);
  const [pluginWords, setPluginWords] = useState<Set<string>>(new Set());
  const [isRemoveMediaModalOpen, setIsRemoveMediaModalOpen] = useState(false);

  const visibleTextPluginOptions = useMemo(
    () => getVisibleTextPluginOptions(event, massText.campaignId),
    [event, massText.campaignId]
  );

  const numDetectedExternalUrls = useMemo(
    () => numDetectedExternalLinks(convertedMessage, accountData.trustedSender),
    [accountData.trustedSender, convertedMessage]
  );

  const singleLinkNotAtEnd = useMemo(
    () => detectedSingleLinkNotAtEnd(convertedMessage),
    [convertedMessage]
  );

  const productAttachmentLinks = useMemo(() => {
    const allUserProducts = userRelatedProducts(
      events,
      accountData.uid,
      tickets
    );
    const allEventIds = event
      ? allUserProducts.map((event) => event.id).concat(event.id)
      : allUserProducts.map((event) => event.id);
    return getEventAttachmentLinks(convertedMessage, allEventIds);
  }, [events, tickets, accountData.uid, event, convertedMessage]);

  const showMinimizeButton = useMemo(() => {
    const bannerCount =
      (singleLinkNotAtEnd ? 1 : 0) +
      (numDetectedExternalUrls > 0 ? 1 : 0) +
      productAttachmentLinks.length;
    return bannerCount > 3;
  }, [
    singleLinkNotAtEnd,
    numDetectedExternalUrls,
    productAttachmentLinks.length,
  ]);

  useAsyncOnMount(async () => {
    // add detected markit links to recognition table, as long as it isn't the non subscriber reply campaign
    if (!isPresetNoRecipientsAutomations(massText.campaignId)) {
      const convertedMessageSplit =
        splitPluginTextInputMessage(convertedMessage);
      const pluginsToAdd: Set<string> = new Set<string>(
        visibleTextPluginOptions.map((option) => option.name)
      );
      for (let i = 0; i < convertedMessageSplit.length; i++) {
        if (
          containsTrustedMarkitEventLink(
            convertedMessageSplit[i],
            accountData.trustedSender
          )
        ) {
          pluginsToAdd.add(convertedMessageSplit[i].trim());
        }
      }

      setPluginWords(pluginsToAdd);
    }
  });

  const setCustomImage = useCallback(
    (customImage: string) =>
      updateMassTextSettings({
        mediaUrl: customImage,
      }),
    [updateMassTextSettings]
  );

  const clearAttachmentOnPress = useCallback(
    (attachmentLink: string) => {
      const lines = convertedMessage.split(/(\n)/);
      const processedLines = lines.map((line) => {
        if (line === "\n") return line;
        const words = line.split(" ");
        const filteredWords = words.filter((word) => word !== attachmentLink);
        return filteredWords.join(" ");
      });
      updateMassTextSettings({ message: processedLines.join("") });
    },
    [convertedMessage, updateMassTextSettings]
  );

  const closeOnPress = useCallback(() => {
    setShowAttachmentModal(false);
    setDefaultAttachmentId(undefined);
    setDefaultAttachmentListView(0);
  }, []);

  // Contains additional logic to handle ticket link and calendar invite
  const finalizeAttachmentOnPress = useCallback(
    (itemId: string) => {
      const isTicketLink =
        itemId === MassTextAttachmentId.TICKET_LINK ||
        itemId === MassTextAttachmentId.CALENDAR_INVITE;
      const eventId = isTicketLink && event ? event.id : itemId;

      // Check if the item is already attached to the message (same eventId)
      if (
        itemId !== MassTextAttachmentId.CONTACT_CARD &&
        getEventAttachmentLinks(convertedMessage, [eventId]).length > 0
      ) {
        setDuplicateAlert(true);
        return;
      }

      const attachmentLink = getMassTextAttachmentLink(
        eventId,
        accountData.trustedSender,
        accountData.username,
        itemId === MassTextAttachmentId.TICKET_LINK
          ? MassTextAttachmentType.TICKET_LINK
          : itemId === MassTextAttachmentId.CALENDAR_INVITE
            ? MassTextAttachmentType.CALENDAR_INVITE
            : undefined
      );

      const newPluginWords = pluginWords.add(attachmentLink.trim());
      setPluginWords(newPluginWords);

      if (itemId === MassTextAttachmentId.CONTACT_CARD) {
        updateMassTextSettings({
          mediaUrl: accountData.customer.contactCardUrl,
        });
      } else {
        const endsWithWhitespace = /\s$/.test(massText.message);
        const messageWithEventURL =
          massText.message +
          (massText.message.length === 0 || endsWithWhitespace ? "" : " ") +
          attachmentLink;
        updateMassTextSettings({ message: messageWithEventURL });
      }
      closeOnPress();
      showNotificationBanner(
        dispatch,
        "Attachment Added!",
        NotificationType.AFFIRMATIVE
      );
    },
    [
      event,
      accountData.trustedSender,
      accountData.username,
      accountData.customer.contactCardUrl,
      pluginWords,
      closeOnPress,
      dispatch,
      updateMassTextSettings,
      massText.message,
      convertedMessage,
    ]
  );

  const onAttachmentClick = useCallback(
    (key: MassTextAttachmentType | string) => {
      setDefaultAttachmentListView(defaultProductListView[key] ?? 0);
      if (!(key in defaultProductListView)) setDefaultAttachmentId(key);
      setShowAttachmentModal(true);
    },
    [setDefaultAttachmentId]
  );

  const onApplyTextComposerSuggestion = useCallback(
    (rewrittenText: string) => {
      updateMassTextSettings({ message: rewrittenText });
    },
    [updateMassTextSettings]
  );

  return (
    <>
      <div
        style={{
          backgroundColor: Colors.WHITE,
          paddingInline: 14,
          overflow: "hidden",
          flex: 1,
        }}
        className="ColumnNormal"
      >
        <div style={{ flexGrow: 1, overflowY: "auto", paddingBottom: 14 }}>
          {massTextAttachments !== undefined ? (
            <InsertPluginTextInput
              trustedSender={accountData.trustedSender}
              message={convertedMessage}
              setMessage={(message: string) =>
                updateMassTextSettings({ message: message })
              }
              placeholder={
                isPresetNoRecipientsAutomations(massText.campaignId)
                  ? "Put your message here!"
                  : massTextEmptyPlaceholder(
                      campaign?.id === DEFAULT_LINK_SUBMISSION_TEXTS_CAMPAIGN_ID
                        ? EventType.GENERIC_LINK
                        : campaign?.id === DEFAULT_EVENT_TEXTS_CAMPAIGN_ID
                          ? EventType.MARKIT
                          : event?.eventType
                    )
              }
              pluginWords={pluginWords}
              image={massText.mediaUrl}
              removeImage={() => setIsRemoveMediaModalOpen(true)}
              cursorPosition={cursorPosition}
              setCursorPosition={setCursorPosition}
              campaignId={massText.campaignId}
              event={event}
              fixedBottomText={
                massText.campaignId === NON_SUBSCRIBER_REPLY_CAMPAIGN_ID ? (
                  <PopupModalMessageTag
                    message={subscribeProfileDefault(accountData.username)}
                    popupTitle="Profile Link Included"
                    popupSubtext="The link to subscribe to your profile will be automatically appended to the end of your message so that people can subscribe."
                  />
                ) : numFollowersNotCompliant > 0 ||
                  isInitialTriggerText(massText, campaign?.initialTextId) ? (
                  <ComplianceMessageTag
                    alternateComplianceMessage={
                      massText.subSelectionIds.length > 0
                        ? "If this is the first text someone has been sent, this compliance message must be included"
                        : massText.type === MassTextType.TRIGGERTEXT
                          ? `This additional default message ${
                              massText.eventRefId ? "may" : "will"
                            } be added to this text when sent for the first time.`
                          : `${numFollowersNotCompliant} of your selected recipients have not received a compliance message. For these recipients, this additional default message will be added to your text.`
                    }
                    alternateContactCardMessage={
                      massText.type === MassTextType.MASSTEXT
                        ? `${
                            numFollowersNotCompliant || "Some"
                          } of your selected recipients ${
                            numFollowersNotCompliant === 1 ? "is" : "are"
                          } getting their first text from you. A contact card will be included and will use 3 extra credits per recipient.`
                        : undefined
                    }
                    mayBeIncluded={massText.eventRefId !== ""}
                  />
                ) : undefined
              }
            />
          ) : (
            <div className="Centering" style={{ height: "100%" }}>
              <CircularProgress style={{ color: Colors.GRAY1 }} size={24} />
            </div>
          )}
        </div>
        <div
          className="ColumnNormal"
          style={{
            position: "sticky",
            bottom: 14,
            left: 14,
            right: 14,
            paddingTop: 24,
          }}
        >
          {showMinimizeButton ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                cursor: "pointer",
              }}
              onClick={() => setMinimizeBanners(!minimizeBanners)}
            >
              <DropdownArrowIcon
                isOpen={minimizeBanners}
                color={Colors.BLACK}
                height={14}
              />
            </div>
          ) : null}
          <TextComposerCard
            draftText={massText.message}
            eventRefId={massText.eventRefId}
            onApplySuggestion={onApplyTextComposerSuggestion}
          />
          <div
            className="ColumnNormal"
            style={{
              gap: 10,
              paddingTop:
                numDetectedExternalUrls > 0 ||
                singleLinkNotAtEnd ||
                productAttachmentLinks.length > 0
                  ? 10
                  : 0,
            }}
          >
            <ExpandableBox
              innerContainerStyles={{
                gap: 10,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {!minimizeBanners ? (
                <>
                  {singleLinkNotAtEnd ? (
                    <StandardBorderedContainer
                      containerStyles={styles.itemContainer}
                    >
                      <div className="AlignedRow" style={{ gap: 10 }}>
                        <div
                          style={styles.blueIconContainer}
                          className="Centering"
                        >
                          <Icon
                            icon="mdi:link-variant-remove"
                            height={27}
                            style={{ color: Colors.BLUE5 }}
                          />
                        </div>
                        <div
                          className="ColumnNormal"
                          style={{ flex: 1, gap: 4 }}
                        >
                          <span className="bodyMedium">
                            Link detected in middle of the message
                          </span>
                          <span className="smallBodySubtext">
                            We recommend attaching the link to the end of the
                            message so that it will show as a link preview when
                            sent to your recipients.
                          </span>
                        </div>
                      </div>
                    </StandardBorderedContainer>
                  ) : null}
                  {numDetectedExternalUrls > 0 ? (
                    <StandardBorderedContainer
                      containerStyles={styles.itemContainer}
                    >
                      <div className="AlignedRow" style={{ gap: 10 }}>
                        <div
                          style={styles.blueIconContainer}
                          className="Centering"
                        >
                          <Icon
                            icon="mdi:shield-link-variant"
                            height={27}
                            style={{ color: Colors.BLUE5 }}
                          />
                        </div>
                        <div className="ColumnNormal" style={{ gap: 4 }}>
                          <span className="bodyMedium">
                            ({numDetectedExternalUrls}){" "}
                            {handlePluralString(
                              "Link",
                              numDetectedExternalUrls
                            )}{" "}
                            Detected
                          </span>
                          <span className="smallBodySubtext">
                            We auto-convert any links you paste into Markit
                            short links.
                            <br />
                            This let&apos;s you track activity and analytics
                            when you send a text.
                          </span>
                        </div>
                      </div>
                    </StandardBorderedContainer>
                  ) : null}
                  {productAttachmentLinks.map((attachment, index) => {
                    const foundEvent = events.find((event) =>
                      attachment.includes(event.id)
                    );
                    const { type } = attachmentDetails(
                      attachment,
                      foundEvent?.eventType
                    );
                    const eventTitle = foundEvent?.title || "Markit link";

                    return (
                      <StandardBorderedContainer
                        key={index}
                        containerStyles={styles.itemContainer}
                      >
                        <div className="AlignedRowSpaced">
                          <div className="AlignedRow" style={{ gap: 10 }}>
                            <StandardBorderedContainer
                              containerStyles={{
                                borderRadius: 8,
                                backgroundColor: Colors.WHITE,
                                height: 37,
                                width: 37,
                                display: "flex",
                              }}
                              className="Centering"
                            >
                              <Icon
                                icon="feather:paperclip"
                                height={20}
                                style={{ color: Colors.BLACK }}
                              />
                            </StandardBorderedContainer>
                            <div
                              className="ColumnNormal"
                              style={{ gap: 2, flex: 1 }}
                            >
                              <span className="bodyMedium">
                                {type === MassTextAttachmentType.GENERIC_LINK
                                  ? "Form"
                                  : type === MassTextAttachmentType.LINK
                                    ? "Event Link"
                                    : type ===
                                        MassTextAttachmentType.TICKET_LINK
                                      ? "Link to Ticket"
                                      : type}
                              </span>
                              <div className="AlignedRow">
                                <h4 style={styles.smallSubtext}>
                                  {type === MassTextAttachmentType.TICKET_LINK
                                    ? "Unique link to each recipient's ticket"
                                    : type ===
                                        MassTextAttachmentType.CALENDAR_INVITE
                                      ? "Unique link to each recipient's ticket to add to calendar"
                                      : eventTitle}
                                </h4>
                              </div>
                            </div>
                          </div>
                          <div
                            style={{ cursor: "pointer" }}
                            onClick={() => clearAttachmentOnPress(attachment)}
                          >
                            <Icon
                              icon="ion:close-outline"
                              height={24}
                              style={{ color: Colors.BLACK }}
                            />
                          </div>
                        </div>
                      </StandardBorderedContainer>
                    );
                  })}
                </>
              ) : null}
            </ExpandableBox>
            <TextCreditsNotice numCredits={numCredits} />
            <AttachmentsActionBar
              // Adding subscribe profile default or compliance message based on campaignId and massText type
              // Also if compliance credit notice should be showed, add it
              message={finalMessageWithAppendages}
              numCredits={numCredits}
              image={massText.mediaUrl}
              setImage={setCustomImage}
              actionItemOnPress={onAttachmentClick}
              attachmentSourceType={
                massText.eventRefId
                  ? AttachmentSourceType.EVENT_TEXT
                  : AttachmentSourceType.MASS_TEXT
              }
              disableEventLinks={disableEventLinks}
              hideActionItems={
                massText.campaignId === NON_SUBSCRIBER_REPLY_CAMPAIGN_ID
              }
              event={event}
              showComplianceCreditNotice={showComplianceCreditNotice}
            />
          </div>
        </div>
      </div>
      <ConfirmDeleteModal
        heading="Remove Media?"
        subtext="This cannot be undone"
        deleteButtonText={"Remove"}
        hideModal={!isRemoveMediaModalOpen}
        setIsVisible={(open: boolean) => {
          setIsRemoveMediaModalOpen(open);
        }}
        deleteOnPress={() => {
          setCustomImage("");
        }}
        theme={theme}
      />
      {duplicateAlert ? (
        <AlertContainer
          headerComp="Already have a link to this on the message!"
          subHeaderComp="We detected that you already have a link to this attachment on the message. We enforce this to ensure that multiple of the same link are not added."
          closeModal={() => setDuplicateAlert(false)}
          closeOnOutsidePress={true}
          hideModal={!duplicateAlert}
        />
      ) : null}
      {showAttachmentModal ? (
        <MassTextAttachmentsPanel
          closeOnPress={closeOnPress}
          defaultListView={defaultAttachmentListView}
          defaultAttachmentId={defaultAttachmentId}
          finalizeAttachment={finalizeAttachmentOnPress}
          event={event}
        />
      ) : null}
    </>
  );
};
